import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

import JalaliDateField from "../Components/JalaliDateField";
const IncomeAdd = ({ open, onClose, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleAdd, loading, resetForm } = useAdd("shop/incomes", token);

  const { data: shopsData, isLoading: shopsLoading } = useFetchObjects(
    ["shops", "income-add"],
    "shop/shop?page_size=all",
    token
  );
  const { data: currenciesData, isLoading: currenciesLoading } = useFetchObjects(
    ["currencies", "income-add"],
    "shop/currencies?page_size=all",
    token
  );

  const shops = shopsData?.results || shopsData || [];
  const currencies = currenciesData?.results || currenciesData || [];

  const [selectedShopId, setSelectedShopId] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [incomeDate, setIncomeDate] = useState("");
  const [notes, setNotes] = useState("");

  const shopCurrencies = useMemo(
    () => currencies.filter((cur) => String(cur.shop) === String(selectedShopId)),
    [currencies, selectedShopId]
  );
  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "income-add", selectedShopId],
    selectedShopId ? `shop/accounts/?page_size=all&shop=${selectedShopId}` : "",
    token
  );
  const accounts = accountsData?.results || accountsData || [];
  const selectedAccount = useMemo(
    () => accounts.find((acc) => String(acc.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );

  useEffect(() => {
    if (!selectedShopId) {
      setSelectedCurrency("");
      setSelectedAccountId("");
      return;
    }
    if (selectedCurrency && !shopCurrencies.some((cur) => String(cur.id) === String(selectedCurrency))) {
      setSelectedCurrency("");
    }
    if (selectedAccountId && !accounts.some((acc) => String(acc.id) === String(selectedAccountId))) {
      setSelectedAccountId("");
    }
  }, [selectedShopId, selectedCurrency, selectedAccountId, shopCurrencies, accounts]);

  useEffect(() => {
    if (!selectedAccountId && accounts.length > 0) {
      setSelectedAccountId(String(accounts[0].id));
    }
  }, [accounts, selectedAccountId]);

  const { data: directRateData } = useFetchObjects(
    ["income-rate-direct", selectedShopId, selectedCurrency, selectedAccount?.currency],
    selectedShopId && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedCurrency}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["income-rate-reverse", selectedShopId, selectedCurrency, selectedAccount?.currency],
    selectedShopId && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrency}`
      : "",
    token
  );

  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrency) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrency;
    if (String(selectedCurrency) === String(accountCurrencyId)) {
      return "1";
    }

    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrency, selectedAccount, directRateData, reverseRateData]);

  useEffect(() => {
    setExchangeRate(exchangeRatePreview || "");
  }, [exchangeRatePreview, selectedCurrency, selectedAccountId]);

  const convertedAmount = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  const currentAccountBalance = useMemo(() => {
    const balance = Number(selectedAccount?.balance);
    if (!Number.isFinite(balance)) {
      return "";
    }
    return balance.toFixed(2);
  }, [selectedAccount]);

  const balanceAfterIncome = useMemo(() => {
    const balance = Number(selectedAccount?.balance);
    const enteredAmount = Number(amount);
    if (!Number.isFinite(balance) || !Number.isFinite(enteredAmount)) {
      return "";
    }
    return (balance + enteredAmount).toFixed(2);
  }, [selectedAccount, amount]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", selectedShopId);
    formData.append("currency", selectedCurrency);
    formData.append("account", selectedAccountId);
    formData.append("amount", amount);
    formData.append("exchange_rate", exchangeRate);
    formData.append("income_date", incomeDate);
    formData.append("notes", notes);
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedShopId("");
      setSelectedCurrency("");
      setSelectedAccountId("");
      setAmount("");
      setExchangeRate("");
      setIncomeDate("");
      setNotes("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("income.addIncome")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          <TextField
            select
            margin="dense"
            label={t("income.selectBranch")}
            fullWidth
            required
            value={selectedShopId}
            onChange={(e) => setSelectedShopId(e.target.value)}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {shopsLoading ? (
              <MenuItem value=""><CircularProgress size={20} /></MenuItem>
            ) : (
              shops.map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                margin="dense"
                label={t("income.selectAccount")}
                fullWidth
                required
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                disabled={!selectedShopId || accountLoading}
                SelectProps={selectProps}
                {...getRtlTextFieldProps(formDirection)}
              >
                {accountLoading ? (
                  <MenuItem value=""><CircularProgress size={20} /></MenuItem>
                ) : accounts.length === 0 ? (
                  <MenuItem value="" disabled>{t("income.noAccount")}</MenuItem>
                ) : (
                  accounts.map((acc) => (
                    <MenuItem key={acc.id} value={String(acc.id)}>
                      {acc.name} ({acc.currency_title || "-"})
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label={t("income.amount")}
                type="number"
                fullWidth
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                {...getRtlTextFieldProps(formDirection)}
              />
            </Grid>
          </Grid>

          <TextField
            select
            margin="dense"
            label={t("income.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            disabled={!selectedShopId}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {currenciesLoading ? (
              <MenuItem value=""><CircularProgress size={20} /></MenuItem>
            ) : (
              shopCurrencies.map((cur) => (
                <MenuItem key={cur.id} value={String(cur.id)}>
                  {cur.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("income.exchangeRate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            disabled={!selectedCurrency || !selectedAccountId}
            {...getRtlTextFieldProps(formDirection, {
              inputProps: { min: 0, step: "0.000001" },
            })}
          />

          <TextField
            margin="dense"
            label={t("income.convertedAmount")}
            fullWidth
            value={convertedAmount}
            disabled
            {...getRtlTextFieldProps(formDirection)}
          />

          <TextField
            margin="dense"
            label={t("income.currentAccountBalance", "Current account balance")}
            fullWidth
            value={
              selectedAccount
                ? `${currentAccountBalance || "0.00"} ${selectedAccount.currency_title || ""}`.trim()
                : ""
            }
            disabled
            {...getRtlTextFieldProps(formDirection)}
          />

          <TextField
            margin="dense"
            label={t("income.balanceAfterIncome", "Balance after income")}
            fullWidth
            value={
              selectedAccount
                ? `${balanceAfterIncome || currentAccountBalance || "0.00"} ${selectedAccount.currency_title || ""}`.trim()
                : ""
            }
            disabled
            {...getRtlTextFieldProps(formDirection)}
          />

          {!exchangeRatePreview && selectedCurrency && selectedAccountId && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1, textAlign: fieldTextAlign }}>
              {t("income.missingRate", "No exchange rate found. You can enter it manually.")}
            </Alert>
          )}

          <JalaliDateField mode="datetime"
            margin="dense"
            label={t("income.incomeDate")}
            fullWidth
            required
            value={incomeDate}
            onChange={(e) => setIncomeDate(e.target.value)}
            {...getRtlTextFieldProps(formDirection, {
              InputLabelProps: { shrink: true },
            })}
          />

          <TextField
            margin="dense"
            label={t("income.notes")}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("income.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !selectedShopId ||
            !selectedCurrency ||
            !selectedAccountId ||
            !amount ||
            !exchangeRate ||
            !incomeDate
          }
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t("income.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncomeAdd;
